import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { portalApi, formatApiError } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Car, ShieldCheck, XCircle } from '@phosphor-icons/react';

export default function PortalSetup() {
  const { token } = useParams();
  const { setup } = usePortalAuth();
  const navigate = useNavigate();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await portalApi.verifySetup(token);
        setTokenInfo(r.data);
      } catch (err) {
        setError(formatApiError(err));
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    if (password !== passwordRepeat) {
      toast.error('Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    try {
      await setup(token, password);
      toast.success('Portal-Zugang aktiviert. Willkommen!');
      navigate('/portal');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F7F8FA] to-[#E9EBF3] p-4" data-testid="portal-setup-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-6">
          <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center">
            <Car weight="fill" size={28} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#111418]">Kunden-Portal</h1>
            <p className="text-sm text-[#8A94A6]">Passwort festlegen</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-[#E2E4E9] p-6">
          {error ? (
            <div className="text-center py-6" data-testid="portal-setup-error">
              <XCircle weight="thin" size={48} className="mx-auto text-red-500 mb-3" />
              <h2 className="text-lg font-semibold text-[#111418] mb-1">Link ungültig oder abgelaufen</h2>
              <p className="text-sm text-[#525866] mb-4">{error}</p>
              <Button variant="outline" onClick={() => navigate('/portal/login')} data-testid="portal-setup-back-btn">Zum Login</Button>
            </div>
          ) : !tokenInfo ? (
            <div className="text-center py-6 text-[#525866]">Wird geprüft…</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="portal-setup-form">
              <div className="text-center mb-2">
                <ShieldCheck weight="thin" size={40} className="mx-auto text-emerald-600 mb-2" />
                <p className="text-sm text-[#525866]">Herzlich willkommen,</p>
                <h2 className="text-lg font-semibold text-[#111418]" data-testid="portal-setup-name">{tokenInfo.name}</h2>
                <p className="text-xs text-[#8A94A6] mt-1">Bitte legen Sie ein Passwort für Ihr Kunden-Portal fest.</p>
              </div>
              <div>
                <Label className="overline">E-Mail</Label>
                <Input value={tokenInfo.email} disabled className="input-swiss bg-[#F7F8FA]" data-testid="portal-setup-email" />
              </div>
              <div>
                <Label className="overline">Neues Passwort (min. 8 Zeichen)</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoFocus autoComplete="new-password" data-testid="portal-setup-password" className="input-swiss" />
              </div>
              <div>
                <Label className="overline">Passwort wiederholen</Label>
                <Input type="password" value={passwordRepeat} onChange={(e) => setPasswordRepeat(e.target.value)} required autoComplete="new-password" data-testid="portal-setup-password2" className="input-swiss" />
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded" disabled={busy} data-testid="portal-setup-submit">
                {busy ? 'Wird gespeichert…' : 'Passwort speichern & anmelden'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
